import { Fragment, useState, useEffect } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { KeenIcon } from '@/components';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { selectUserPermissions } from '@/store/authSlice';
import {
  useGetPlatformSettingByIdQuery,
  useUpdatePlatformSettingMutation,
} from '@/services/PlatformSettingsService';
import { languageOptions } from '@/types/cms.types';

const PlatformSettingForm = () => {
  const { currentLayout } = useLayout();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const userPermissions = useSelector(selectUserPermissions);

  const canUpdate = userPermissions?.some((p) =>
    p.permissions?.unique_key?.includes('platform_setting_update')
  );

  const [value, setValue] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const { data: settingData, isLoading: isFetching } =
    useGetPlatformSettingByIdQuery(id || '', {
      skip: !id,
    });

  const [updateSetting, { isLoading: isUpdating }] =
    useUpdatePlatformSettingMutation();

  useEffect(() => {
    if (settingData) {
      setValue(settingData.value);
      if (settingData.key === 'Logo URL') {
        setLogoPreview(settingData.value);
      }
    }
  }, [settingData]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    try {
      let data: FormData | { key: string; value: string | boolean };

      if (settingData?.key === 'Logo URL' && logoFile) {
        const formData = new FormData();
        formData.append('key', settingData.key);
        formData.append('media', logoFile, logoFile.name);
        data = formData;
      } else {
        data = {
          key: settingData!.key,
          value: value,
        };
      }

      await updateSetting({
        id: id!,
        data,
      }).unwrap();
      enqueueSnackbar('Setting updated successfully', { variant: 'success' });
      navigate('/platform-setting');
    } catch {
      enqueueSnackbar('Failed to update setting', { variant: 'error' });
    }
  };

  const handleCancel = () => {
    navigate('/platform-setting');
  };

  if (isFetching) {
    return (
      <div className="container-fixed">
        <div className="card">
          <div className="card-body">
            <div className="text-center py-4">Loading setting...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle text="Edit Setting" />
              <ToolbarDescription>
                Update platform configuration
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              <button
                className="btn btn-sm btn-light flex items-center gap-2 h-10"
                onClick={handleCancel}
              >
                <KeenIcon icon="arrow-left" />
                Back to List
              </button>
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}

      <div className="container-fixed">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Edit {settingData?.key}</h2>
          </div>
          <div className="card-body">
            <div className="space-y-6">
              {settingData?.key === 'Language' ? (
                <div>
                  <label id="language-label" className="form-label mb-3">
                    Select Language
                  </label>
                  <div
                    role="group"
                    aria-labelledby="language-label"
                    className="flex flex-col gap-2"
                  >
                    {languageOptions.map((option) => (
                      <label
                        key={option.value}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="language"
                          value={option.value}
                          checked={value === option.value}
                          onChange={(e) => setValue(e.target.value)}
                          className="radio radio-primary"
                          disabled={!canUpdate}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ) : settingData?.key === 'Logo URL' ? (
                <div>
                  <label htmlFor="logo-upload" className="form-label mb-3">
                    Upload Logo
                  </label>
                  <div className="flex flex-col gap-4">
                    {logoPreview && (
                      <div className="relative w-40 h-40 bg-[#105B78] p-2 rounded-lg flex items-center justify-center">
                        <img
                          src={logoPreview}
                          alt="Logo Preview"
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    )}
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="file-input file-input-bordered w-full max-w-xs"
                      disabled={!canUpdate}
                    />
                  </div>
                </div>
              ) : settingData?.value_type === 'boolean' ? (
                <div>
                  <label
                    id="boolean-setting-label"
                    htmlFor="boolean-setting-input"
                    className="form-label mb-3"
                  >
                    {settingData?.key}
                  </label>
                  <label className="switch">
                    <input
                      id="boolean-setting-input"
                      type="checkbox"
                      checked={value === 'true'}
                      onChange={(e) =>
                        setValue(e.target.checked ? 'true' : 'false')
                      }
                      disabled={!canUpdate}
                      className="cursor-pointer"
                      aria-labelledby="boolean-setting-label"
                    />
                    <span className="slider round "></span>
                  </label>
                </div>
              ) : (
                <div>
                  <label htmlFor="text-setting-input" className="form-label">
                    {settingData?.key}
                  </label>
                  <input
                    id="text-setting-input"
                    type="text"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    className="input input-bordered w-full"
                    disabled={!canUpdate}
                  />
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t">
                <button
                  className="btn btn-light"
                  onClick={handleCancel}
                  disabled={isUpdating}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={isUpdating || !canUpdate}
                >
                  {isUpdating ? (
                    <span className="flex items-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Updating...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <KeenIcon icon="check" className="mr-2" />
                      Update Setting
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export { PlatformSettingForm };
